import Image, { ImageProps } from "next/image";
import clsx from "clsx";
import { getImageLoadingStrategy, getImageSizes, getImageQuality } from "../../lib/image-optimization";

const CMS_BASE = process.env.NEXT_PUBLIC_STRAPI_URL || "http://localhost:1337";

export const absoluteCmsUrl = (path?: string) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/")) return `${CMS_BASE}${path}`;
  return `${CMS_BASE}/${path}`;
};

type BaseImageProps = ImageProps & {
  imagePosition?: string | null;
  isAboveFold?: boolean;
  isCritical?: boolean;
  isHero?: boolean;
  isThumbnail?: boolean;
  breakpoint?: "mobile" | "tablet" | "desktop" | "full";
};

export function BaseImage({
  src,
  alt,
  className,
  imagePosition,
  isAboveFold = false,
  isCritical = false,
  isHero = false,
  isThumbnail = false,
  breakpoint = "desktop",
  loading,
  quality,
  sizes,
  priority,
  ...rest
}: BaseImageProps) {
  const resolved = typeof src === "string" ? absoluteCmsUrl(src) : src;

  if (!resolved || (typeof resolved === "string" && resolved.trim() === "")) {
    return null;
  }

  // If priority is set, use eager loading; otherwise auto-determine loading strategy
  const loadingStrategy = priority ? "eager" : (loading || getImageLoadingStrategy(isAboveFold, isCritical, isHero));

  // Auto-determine quality if not provided
  const imageQuality = quality || getImageQuality(isHero, isThumbnail);

  // Auto-determine sizes if not provided
  const imageSizes = sizes || getImageSizes(breakpoint);

  return (
    <Image
      src={resolved}
      alt={alt || ""}
      className={clsx("object-cover", className)}
      style={{ objectPosition: imagePosition ?? "center center" }}
      loading={loadingStrategy}
      quality={imageQuality}
      sizes={imageSizes}
      priority={priority}
      {...rest}
    />
  );
}
